import {
  Card,
  Collapse,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Typography,
  Space,
  Button,
  DatePicker,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  ADDENDA_FIELDS,
  ADDENDA_OPTIONS,
  petTemplate,
} from "../../../easyapp.constants";

interface AddendaFormProps {
  form: any;
  editMode: boolean;
  selectedPropertyFeeRecord: any;
}

export const AddendaForm: React.FC<AddendaFormProps> = ({ form, editMode, selectedPropertyFeeRecord }) => {
  const selectedAddendas = Form.useWatch("addenda", form) || [];

  if (!selectedAddendas.length) return null;

  const collapseItems = selectedAddendas
    .map((addendum: string) => {
      const label =
        ADDENDA_OPTIONS.find((opt) => opt.value === addendum)?.label ||
        addendum;
      const fields =
        ADDENDA_FIELDS[addendum as keyof typeof ADDENDA_FIELDS] || [];

      if (!fields.length) return null;

      // ---------- Animal Addendum ----------
      if (addendum === "animal") {
        return {
          key: addendum,
          label: <Typography.Text strong>{label}</Typography.Text>,
          children: (
            <Form.List name="pets">
              {(petFields, { add, remove }) => (
                <>
                  <Collapse
                    accordion
                    items={petFields.map(
                      ({ key, name, ...restField }, index) => {
                        const pet = form.getFieldValue(["pets", name]) || {};
                        return {
                          key,
                          label: `Pet ${index + 1}: ${pet.name || ""}`,
                          children: (
                            <>
                              <Row justify="end" align="middle">
                                {editMode && petFields.length > 1 && (
                                  <Col>
                                    <Button
                                      type="text"
                                      icon={<DeleteOutlined />}
                                      danger
                                      onClick={() => remove(name)}
                                    >
                                      Remove
                                    </Button>
                                  </Col>
                                )}
                              </Row>
                              <Row gutter={[16, 16]}>
                                {fields.map((field) => {
                                  const keyName = field.name.replace(
                                    "apa_animal_",
                                    ""
                                  );
                                  let initialValue = pet[keyName];
                                  if (
                                    (initialValue === undefined) &&
                                    keyName === "deposit"
                                  ) {
                                    initialValue =
                                      selectedPropertyFeeRecord?.PetDepositFee ||
                                      "";
                                  }
                                  if (
                                    (initialValue === undefined) &&
                                    keyName === "monthly"
                                  ) {
                                    initialValue =
                                      selectedPropertyFeeRecord?.PetMonthlyFee ||
                                      "";
                                  }
                                  return (
                                    <Col
                                      xs={24}
                                      sm={12}
                                      key={`${field.name}_${key}`}
                                    >
                                      <Form.Item
                                        {...restField}
                                        name={[name, keyName]}
                                        label={field.label}
                                        initialValue={initialValue}
                                      >
                                        {field.type === "number" ? (
                                          <InputNumber
                                            disabled={!editMode}
                                            prefix={
                                              field.name.includes("fee")
                                                ? "$"
                                                : undefined
                                            }
                                            min={0}
                                            style={{ width: "100%" }}
                                          />
                                        ) : field.type === "date" ? (
                                          <DatePicker
                                            disabled={!editMode}
                                            style={{ width: "100%" }}
                                          />
                                        ) : (
                                          <Input disabled={!editMode} />
                                        )}
                                      </Form.Item>
                                    </Col>
                                  );
                                })}
                              </Row>
                            </>
                          ),
                        };
                      }
                    )}
                  />

                  {editMode && petFields.length < 2 && (
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() => add({ ...petTemplate })}
                      style={{ marginTop: 12 }}
                    >
                      Add Another Pet
                    </Button>
                  )}
                </>
              )}
            </Form.List>
          ),
        };
      }

      // ---------- Animal Identification ----------
      if (addendum === "animal_identification") {
        return {
          key: addendum,
          label: <Typography.Text strong>{label}</Typography.Text>,
          children: (
            <Form.List name={["addendaData", addendum]}>
              {() => {
                const pets = form.getFieldValue("pets") || [];
                return (
                  <Collapse
                    accordion
                    items={pets.map((pet: any, index: number) => ({
                      key: index,
                      label: `Pet ${index + 1}: ${pet.name || ""}`,
                      children: (
                        <Row gutter={[16, 16]}>
                          {fields.map((field) => {
                            const keyName = field.name.replace(
                              "aif_animal_",
                              ""
                            );
                            return (
                              <Col
                                xs={24}
                                sm={12}
                                key={`${field.name}_${index}`}
                              >
                                <Form.Item
                                  name={[index, keyName]}
                                  label={field.label}
                                >
                                  <Input disabled={!editMode} />
                                </Form.Item>
                              </Col>
                            );
                          })}
                        </Row>
                      ),
                    }))}
                  />
                );
              }}
            </Form.List>
          ),
        };
      }

      // ---------- Default Addenda ----------
      return {
        key: addendum,
        label: (
          <Space align="center" wrap>
            <Typography.Text strong>{label}</Typography.Text>
          </Space>
        ),
        children: (
          <Row gutter={[16, 16]}>
            {fields.map((field) => (
              <Col xs={24} sm={12} key={field.name}>
                <Form.Item
                  name={["addendaData", addendum, field.name]}
                  label={field.label}
                  initialValue={
                    form.getFieldValue(["addendaData", addendum, field.name]) ||
                    ""
                  }
                >
                  {field.type === "number" ? (
                    <InputNumber
                      disabled={!editMode}
                      prefix={
                        field.name.includes("fee") ||
                        field.name.includes("deposit")
                          ? "$"
                          : undefined
                      }
                      min={0}
                      style={{ width: "100%" }}
                    />
                  ) : field.type === "date" ? (
                    <DatePicker
                      disabled={!editMode}
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <Input disabled={!editMode} />
                  )}
                </Form.Item>
              </Col>
            ))}
          </Row>
        ),
      };
    })
    .filter(Boolean);

  return (
    <Card
      title={<Typography.Title level={4}>Addenda Details</Typography.Title>}
      style={{
        borderRadius: "8px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        marginBottom: 16,
      }}
    >
      <Collapse accordion items={collapseItems as any} />
    </Card>
  );
};